// AiChatV2Pane — full-height chat surface used inside the V3 three-pane
// layout (DataExplorerViewV2). Unlike <AiChat>, this component does NOT
// provision an AiChatProvider; the host wraps the whole view (chat pane
// + run-progress panel) in a single provider so they share session state.
//
// The pane renders:
//   - a thin v2 header (current session title + optional actions slot)
//   - the existing <ChatPane /> (messages + composer/Prompt) which reads
//     from the surrounding AiChatProvider
//
// All chat business logic stays inside ChatPane.

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ChatPane from '../AiChat/ChatPane/ChatPane';
import { useAiChatSessionStore } from '../AiChat/AiChatContext';

export interface AiChatV2PaneProps {
    /** Override the title shown in the header. Defaults to the current session's overview. */
    title?: string;
    /** Optional mono-styled subtitle, e.g. "· 14 sem · BI_Master". */
    subtitle?: string;
    /** Right-aligned slot, e.g. "Pin no dashboard" chip. */
    actions?: React.ReactNode;
    /** Hide the header entirely. */
    hideHeader?: boolean;
    /**
     * Optional content rendered below the prompt input, inside the same
     * column. Mirrors the `bottomSlot` of the v1 AiChat / AiChatFloating:
     * the inner ChatPane keeps its own scroll and the slot lives below
     * it as a fixed-height row. The responsive DataExplorer uses this
     * to mount `<RunProgressStripV2 />` on mobile.
     */
    bottomSlot?: React.ReactNode;
}

export const AiChatV2Pane: React.FC<AiChatV2PaneProps> = ({
    title,
    subtitle,
    actions,
    hideHeader = false,
    bottomSlot,
}) => {
    const sessionOverview = useAiChatSessionStore((s) => s.session?.overview);
    const headerTitle = title ?? sessionOverview ?? 'Nova conversa';

    return (
        <Box
            sx={(theme) => ({
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                backgroundColor: theme.palette.background.default,
            })}
        >
            {!hideHeader && (
                <Box
                    sx={(theme) => ({
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        px: 2.25,
                        py: 1.5,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.default,
                    })}
                >
                    <Typography
                        component="h2"
                        sx={(theme) => ({
                            m: 0,
                            fontSize: 14,
                            fontWeight: 500,
                            color: theme.palette.text.primary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        })}
                    >
                        {headerTitle}
                    </Typography>
                    {subtitle && (
                        <Typography
                            sx={(theme) => ({
                                fontSize: 11,
                                color: theme.palette.text.disabled,
                                fontFamily: theme.typography.fontFamilyMono,
                                whiteSpace: 'nowrap',
                            })}
                        >
                            {subtitle}
                        </Typography>
                    )}
                    <Box sx={{ flex: 1 }} />
                    {actions && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>{actions}</Box>
                    )}
                </Box>
            )}
            {/* `overflow: hidden` (not `scroll`) — ChatPane manages
                its own internal scroll for the transcript and pins
                the Prompt at the bottom. Wrapping with a second
                scroll container would let the prompt drift below
                the visible area on tall transcripts.

                `display: flex` + `flexDirection: column` is required
                here (not just `block`): ChatPane's outer Box uses
                `height: 100%` to size itself, and percentage heights
                only resolve reliably when the parent passes a
                definite height through. A block wrapper that is
                only sized via `flex: 1` in its grandparent doesn't
                guarantee that, which would collapse the chat
                container's `flex: 1, minHeight: 0` and break the
                inner scroll — the symptom is "I can't scroll" or
                "messages spill past the prompt". */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Mirrors AiChat's bottomSlot wrapping: ChatPane keeps
                    its own scroll and pins its prompt at the bottom,
                    while the slot sits beneath it as a fixed-height row.
                    Skipping the wrapper when no slot is requested avoids
                    one extra flex level on the common (desktop) path. */}
                {bottomSlot ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            minHeight: 0,
                        }}
                    >
                        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                            <ChatPane />
                        </Box>
                        {bottomSlot}
                    </Box>
                ) : (
                    <ChatPane />
                )}
            </Box>
        </Box>
    );
};

AiChatV2Pane.displayName = 'AiChatV2Pane';
